import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../hooks/useAuth';
import jobService from '../../utils/jobService';
import { supabase } from '../../utils/supabase';
import { extractJobIdFromSlug } from '../../utils/slugify';
import ApplicationModal from '../job-details/components/ApplicationModal';
import LoadingSkeleton from '../job-details/components/LoadingSkeleton';
import ErrorState from '../job-details/components/ErrorState';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

/**
 * Dedicated Job Application Page
 * Used when job has attached forms requiring a full-page experience
 */
const JobApplication = () => {
  const navigate = useNavigate();
  const { jobSlug } = useParams();
  const { user, userProfile } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);
  
  // Extract job ID from slug (could be short ID or full UUID)
  const jobId = jobSlug ? extractJobIdFromSlug(jobSlug) : null;

  useEffect(() => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login-register?redirect=${encodeURIComponent(`/job-application/${jobSlug}`)}`);
      return;
    }

    if (!jobId) {
      setError('No job ID provided');
      setLoading(false);
      return;
    }

    const loadJob = async () => {
      try {
        setLoading(true);
        const result = await jobService.getJobWithFullDetails(jobId);
        
        if (result?.success && result.data) {
          setJob(result.data);
          
          // Check if user has already applied to this job
          if (user?.id) {
            const { data: existingApplication, error: appError } = await supabase
              .from('job_applications')
              .select('id')
              .eq('job_id', result.data.id)
              .eq('applicant_id', user.id)
              .maybeSingle();
            
            if (!appError && existingApplication) {
              setHasApplied(true);
            }
          }
        } else {
          setError(result?.error || 'Failed to load job details');
        }
      } catch (err) {
        setError('An error occurred while loading the job');
        console.error('Error loading job:', err);
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [jobId, user, navigate, jobSlug]);

  const handleClose = () => {
    // Return to job details page using the same slug format
    if (job?.public_slug) {
      navigate(`/job-details/${job.public_slug}`);
    } else if (job?.id) {
      navigate(`/job-details?jobId=${job.id}`);
    } else {
      navigate('/job-search');
    }
  };

  const handleSubmit = async (applicationData) => {
    try {
      // Add job_id and applicant_id to the application data
      const completeApplicationData = {
        ...applicationData,
        job_id: job.id,
        applicant_id: user.id
      };
      
      const result = await jobService.applyToJob(completeApplicationData);
      
      if (result?.success) {
        // Redirect back to job details with success message
        if (job?.public_slug) {
          navigate(`/job-details/${job.public_slug}?applied=true`);
        } else {
          navigate(`/job-details?jobId=${job.id}&applied=true`);
        }
      } else {
        throw new Error(result?.error || 'Failed to submit application');
      }
    } catch (error) {
      throw error; // Let ApplicationModal handle the error display
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Apply for Job - VA Sprouts</title>
        </Helmet>
        <div className="max-w-4xl mx-auto py-8 px-4">
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Job Not Found - VA Sprouts</title>
        </Helmet>
        <div className="max-w-4xl mx-auto py-8 px-4">
          <ErrorState 
            message={error || 'Job not found'}
            onRetry={() => navigate('/job-search')}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Apply for {job.title} - VA Sprouts</title>
        <meta name="description" content={`Submit your application for ${job.title} at ${job.company_name || 'this company'}`} />
      </Helmet>
      
      <div className="min-h-screen bg-background pb-8">
        {/* Header Bar */}
        <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="text-text-primary" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-text-primary">Apply for Position</h1>
                <p className="text-sm text-text-secondary">{job.title} • {job.company_name || job.employer?.company_name || job.employer?.full_name}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Show already applied message if user has already applied */}
        {hasApplied ? (
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="bg-surface rounded-lg border border-border p-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center">
                  <Icon name="Check" size={32} className="text-success" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-text-primary mb-3">Already Applied</h2>
              <p className="text-text-secondary mb-6 max-w-md mx-auto">
                You have already submitted an application for this position. The employer will review your application and contact you if they're interested in moving forward.
              </p>
              <div className="flex justify-center space-x-4">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                >
                  Back to Job Details
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/job-search')}
                >
                  Browse More Jobs
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <ApplicationModal
            job={job}
            userProfile={userProfile}
            onClose={handleClose}
            onSubmit={handleSubmit}
            asPage={true}
          />
        )}
      </div>
    </>
  );
};

export default JobApplication;
